import type { DesktopDeviceIdentity, RegisterCmmdDeviceParams } from "@cmmd-forge/contracts";
import { readStoredValue, writeStoredValue } from "./lib/storage";

const DEVICE_KEY_STORAGE_KEY = "forge.deviceRegistry.deviceKey";
const RUNTIME_ID_STORAGE_KEY = "forge.deviceRegistry.runtimeId";

type Platform = RegisterCmmdDeviceParams["device"]["platform"];
type DeviceType = RegisterCmmdDeviceParams["device"]["deviceType"];

export interface LocalDeviceRegistrationInput {
  readonly desktopEndpointUrl?: string | null;
  readonly desktopAvailable: boolean;
  readonly desktopIdentity?: DesktopDeviceIdentity | null;
  readonly includeExecutableRuntime?: boolean;
}

export function buildLocalDeviceRegistration(
  input: LocalDeviceRegistrationInput,
): RegisterCmmdDeviceParams {
  const deviceKey = resolveStableId(
    DEVICE_KEY_STORAGE_KEY,
    input.desktopIdentity?.deviceKey,
    "device",
  );
  const runtimeId = resolveStableId(
    RUNTIME_ID_STORAGE_KEY,
    input.desktopIdentity?.runtimeId,
    "runtime",
  );
  const hostKey = normalizeStableId(input.desktopIdentity?.hostKey);
  const platform = detectPlatform();
  const deviceType = input.desktopAvailable ? "desktop" : detectDeviceType();
  const displayName = input.desktopAvailable
    ? (normalizeDesktopDisplayName(input.desktopIdentity) ?? desktopDisplayName(platform))
    : browserDisplayName(platform);

  return {
    device: {
      ...(hostKey ? { hostKey } : {}),
      deviceKey,
      displayName,
      platform,
      deviceType,
      metadata: {
        clientKind: input.desktopAvailable ? "forge-desktop" : "forge-web",
        userAgent: window.navigator.userAgent,
        origin: window.location.origin,
        ...(input.desktopIdentity
          ? {
              desktopIdentity: {
                hostName: input.desktopIdentity.hostName,
                computerName: input.desktopIdentity.computerName,
                platform: input.desktopIdentity.platform,
              },
            }
          : {}),
      },
    },
    ...(input.desktopAvailable && input.includeExecutableRuntime !== false
      ? {
          runtime: {
            runtimeId,
            runtimeKind: "desktop-local" as const,
            displayName: "Local Forge Desktop",
            endpointUrl: input.desktopEndpointUrl ?? null,
            status: "online" as const,
            metadata: {
              origin: window.location.origin,
            },
          },
        }
      : {}),
  };
}

export async function buildCurrentControlDeviceRegistration(): Promise<RegisterCmmdDeviceParams> {
  const desktopIdentity = (await window.desktopBridge?.getDeviceIdentity?.()) ?? null;
  return buildLocalDeviceRegistration({
    desktopAvailable: Boolean(window.desktopBridge),
    desktopIdentity,
    includeExecutableRuntime: false,
  });
}

function normalizeStableId(value: string | null | undefined): string | null {
  const normalized = value?.trim() ?? "";
  return normalized.length > 0 ? normalized : null;
}

function resolveStableId(
  key: string,
  nativeValue: string | null | undefined,
  prefix: string,
): string {
  const normalizedNativeValue = normalizeStableId(nativeValue);
  if (normalizedNativeValue) {
    writeStoredValue(key, normalizedNativeValue);
    return normalizedNativeValue;
  }
  return readStableId(key, prefix);
}

export function getStoredLocalDeviceRuntimeId(): string | null {
  return readStoredValue(RUNTIME_ID_STORAGE_KEY);
}

export function getStoredLocalDeviceKey(): string | null {
  return readStoredValue(DEVICE_KEY_STORAGE_KEY);
}

function readStableId(key: string, prefix: string): string {
  const existing = readStoredValue(key);
  if (existing) return existing;
  const next = `${prefix}_${crypto.randomUUID()}`;
  writeStoredValue(key, next);
  return next;
}

function normalizeDesktopDisplayName(
  identity: DesktopDeviceIdentity | null | undefined,
): string | null {
  const displayName = identity?.displayName?.trim() ?? "";
  return displayName.length > 0 ? displayName : null;
}

function detectPlatform(): Platform {
  const userAgent = window.navigator.userAgent.toLowerCase();
  if (userAgent.includes("iphone") || userAgent.includes("ipad")) return "ios";
  if (userAgent.includes("android")) return "android";
  if (userAgent.includes("mac")) return "macos";
  if (userAgent.includes("win")) return "windows";
  if (userAgent.includes("linux")) return "linux";
  return "unknown";
}

function detectDeviceType(): DeviceType {
  const userAgent = window.navigator.userAgent.toLowerCase();
  if (userAgent.includes("ipad") || userAgent.includes("tablet")) return "tablet";
  if (userAgent.includes("mobi") || userAgent.includes("iphone") || userAgent.includes("android")) {
    return "mobile";
  }
  return "browser";
}

function desktopDisplayName(platform: Platform): string {
  if (platform === "macos") return "Mac";
  if (platform === "windows") return "Windows PC";
  if (platform === "linux") return "Linux Desktop";
  return "Desktop";
}

function browserDisplayName(platform: Platform): string {
  if (platform === "web") return "Browser";
  if (platform === "macos") return "Mac Browser";
  if (platform === "windows") return "Windows Browser";
  if (platform === "linux") return "Linux Browser";
  return "Browser";
}
